// src/pages/Future.js
import React, { useEffect } from "react";

// Import images
import future1 from "../assets/future1.webp";
import future2 from "../assets/future2.webp";
import future3 from "../assets/future3.webp";

// 🔹 Reusable Section Component
const SectionWithImages = ({ id, title, text, images, reverse = false, labels = [] }) => {
    return (
        <div id={id} className={`flex flex-col ${reverse ? "sm:flex-row-reverse" : "sm:flex-row"} items-center text-white bg-gray-900 p-12 sm:p-20 gap-8`}>
            {/* Full-Width Text for Image-less Sections */}
            <div className={`w-full ${images.length === 0 ? "text-center" : "sm:w-1/2 text-left"}`}>
                <h2 className="text-4xl sm:text-5xl font-bold mb-6 animate-fadeIn">{title}</h2>
                <p className="text-lg sm:text-xl animate-fadeIn animate-delay-300 text-justify whitespace-pre-line">
                    {text || "More details coming soon."}
                </p>
            </div>

            {/* Images Section with Labels (only if images exist) */}
            {images.length > 0 && (
                <div className={`w-full sm:w-1/2 grid grid-cols-1 ${images.length > 1 ? "sm:grid-cols-2" : ""} gap-4`}>
                    {images.map((img, index) => (
                        <div key={index} className="flex flex-col items-center">
                            <img
                                src={img}
                                alt={`${title} ${index + 1}`}  // Simplified alt text
                                className="rounded-lg shadow-lg transform hover:scale-105 transition duration-300 w-full h-66 sm:h-98 object-cover"
                            />
                            {/* Add Figure Label if Provided */}
                            {labels[index] && <p className="mt-2 text-sm sm:text-base text-gray-300">{labels[index]}</p>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const Future = () => {
    useEffect(() => {
        const timer = setTimeout(() => {
            // Add your scroll indicator logic here if necessary
        }, 5000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const enableSound = () => {
            // Enable sound logic here if needed
            document.removeEventListener("click", enableSound);
        };
        document.addEventListener("click", enableSound);
        return () => document.removeEventListener("click", enableSound);
    }, []);

    return (
        <div className="w-full">
            {/* 🔹 Our Future Plan */}
            <SectionWithImages
                id="our-future-plan"
                title="Our Future Plan"
                text="To expand the sustainable applications of microalgae in the future, we will focus on three key areas: biofuel, wastewater treatment, and biodegradable materials. By furthering these projects, we hope to develop a comprehensive strategy that tackles environmental issues and makes the most of microalgae's potential for a more sustainable future."
                images={[]}
                reverse={false}
            />

            {/* 🔹 Bioenergy Section */}
            <SectionWithImages
                id="bioenergy"
                title="Bioenergy and Biofuels:"
                text="Utilizing the high lipid content of microalgae to produce biodiesel is the goal of our upcoming projects. Unlike conventional biofuel crops, microalgae may be grown on non-arable land, so they don't compete with food production. Their sustainability can be further increased by growing them with brackish water or even wastewater. This strategy offers a substitute energy source that facilitates the transition to renewable fuels, hence reducing greenhouse gas emissions and dependency on fossil fuels."
                images={[future1]}
                reverse={true}
            />

            {/* 🔹 Wastewater Treatment */}
            <SectionWithImages
                id="wastewater-treatment"
                title="Wastewater Treatment:"
                text="The inherent capacity of microalgae to absorb nutrients and pollutants makes them perfect for wastewater purification, which is what we want to use. The effective CO2 capture by microalgae greatly aids in carbon sequestration. Additionally, they reduce water pollutants by absorbing nitrogen, phosphorus, and heavy metals from effluent."
                images={[future2]}
                reverse={false}
            />

            {/* 🔹 Bioplastics and Bioproducts Section */}
            <SectionWithImages
                id="bioplastics"
                title="Bioplastics and Bioproducts:"
                text="Our goal is to create bio-polymers from microalgae that can be utilized in biodegradable plastics. Made from microalgae, these bioplastics are highly effective, renewable, and completely biodegradable materials. They drastically lessen the impact on the environment by offering a very sustainable substitute for conventional plastics."
                images={[future3]}
                reverse={true}
            />
        </div>
    );
};

export default Future;